/* @flow */

import React, { useContext } from "react";
import { Link } from "react-router-dom";
import Wrapper from "components/Wrapper";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import Logo from "components/AppHeader/Logo";
import Button from "components/Button";
import ButtonLink from "components/ButtonLink";
import { StoreInfoContext } from "entrypoint/shared";

import Facebook from "icons/facebook.svg";
import Instagram from "icons/instagram.svg";
import LinkedIn from "icons/linkedIn.svg";

import styles from "./styles.scss";

type Props = {
  className: string,
  setToggleCookieDialogue: (value: boolean) => void,
};

const AppFooter = ({ className = "", setToggleCookieDialogue }: Props): React$Node => {
  const t = useTranslate();
  const { categories } = useContext(StoreInfoContext);

  const needHelp = () => (
    <section className={styles.needHelp}>
      <p className={styles.title}>{t("CUSTOMER_SERVICE.TEXT")}</p>

      <div className={styles.nav}>
        <p>{t("FOOTER.NEED_HELP.TITLE")}</p>
        <ButtonLink variant="primary" size="small" className={styles.link} to={t("CUSTOMER_SERVICE.LINK")}>
          {t("FOOTER.NEED_HELP.CONTACT")}
        </ButtonLink>
        <ul className={styles.helpLinks}>
          <li>
            <Link
              to="/privacy-policy"
              className={styles.link}
            >
              {t("FOOTER.NEED_HELP.PRIVACY")}
            </Link>
          </li>
          <li>
            <Link
              to="/privacy-policy"
              className={styles.link}
            >
              {t("FOOTER.NEED_HELP.COOKIE_POLICY")}
            </Link>
          </li>
          <li>
            <Button
              className={styles.link}
              onClick={() => setToggleCookieDialogue(true)}
            >
              {t("FOOTER.NEED_HELP.MANAGE_COOKIES")}
            </Button>
          </li>
          <li>
            <Link className={styles.link} to="/accessibility">
              {t("FOOTER.ACCESSIBILITY")}
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );

  const storeCategories = () => (
    <section className={styles.categories}>
      <p className={styles.title}>{t("FOOTER.LINKS.CATEGORIES")}</p>
      <div className={styles.nav}>
        <ul>
          {categories.map(category => (
            <li key={category.name}>
              <Link
                to={category.url}
                className={styles.link}
              >
                {category.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );

  const logo = () => (
    <section className={styles.copyright}>
      <Logo className={styles.logo} />

      <div className={styles.socialmediaLinks}>
        <ul>
          <li>
            <a
              className={styles.link}
              aria-label={t("FOOTER.SOCIAL.FACEBOOK")}
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.facebook.com/SectorAlarmSverige"
            >
              <span className={cn(styles.social, styles.facebook)} />
              <Facebook />
            </a>
          </li>
          <li>
            <a
              className={styles.link}
              aria-label={t("FOOTER.SOCIAL.LINKEDIN")}
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.linkedin.com/company/sector-alarm-sverige"
            >
              <span className={cn(styles.social, styles.linkedin)} />
              <LinkedIn />
            </a>
          </li>
          <li>
            <a
              className={styles.link}
              aria-label={t("FOOTER.SOCIAL.INSTAGRAM")}
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.instagram.com/sectoralarmsverige"
            >
              <span className={cn(styles.social, styles.instagram)} />
              <Instagram />
            </a>
          </li>
        </ul>
      </div>
    </section>
  );

  return (
    <footer className={cn(styles.block, className)} role="contentinfo">
      <nav aria-label={t("FOOTER.LINKS.NAVIGATION")}>
        <div className={styles.large}>
          <Wrapper className={styles.wrapper}>
            <div className={styles.col}>
              {needHelp()}
            </div>
            <div className={styles.col}>
              {storeCategories()}
            </div>
            <div className={styles.col}>
              {logo()}
            </div>
          </Wrapper>
        </div>

        <div className={styles.small}>
          <Wrapper className={styles.wrapper}>
            <div className={styles.col}>
              {needHelp()}
              {storeCategories()}
            </div>
          </Wrapper>

          <div className={styles.col}>
            {logo()}
          </div>
        </div>
        <div className={styles.bottomBar}>
          <p dangerouslySetInnerHTML={{ __html: t("FOOTER.COPYRIGHT", { year: new Date().getFullYear() }) }} />
        </div>
      </nav>
    </footer>
  );
};

export default AppFooter;
